'use client';

import Image from 'next/image';
import {useTranslations} from 'next-intl';
import {type FC, memo} from 'react';
import {StatIcon} from '@/components/atoms/StatIcon';
import useBreakpoints from '@/hooks/useBreakpoints';
import {Modal} from '@/utils/antd-imports';
import {formatNumber} from '@/utils/helpers/formatNumber';

interface ShareRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  reward: {
    poe: number;
    coins: number;
  };
}

/**
 * @description Модальное окно для отображения награды за шеринг экспириенса/айтема
 * @example
 * <ShareRewardModal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   reward={{ poe: 100, coins: 50 }}
 * />
 */
export const ShareRewardModal: FC<ShareRewardModalProps> = memo(({isOpen, onClose, reward}) => {
  const {lg, tablet} = useBreakpoints();
  const tModal = useTranslations('experiences.detail.rewardModal');

  const calcWidth = () => {
    if (lg) {
      return '560px';
    }
    if (tablet) {
      return '400px';
    }
    return '340px';
  };

  const hasPoe = reward.poe > 0;
  const hasCoins = reward.coins > 0;

  return (
    <Modal
      open={isOpen}
      centered={true}
      closable={false}
      onCancel={onClose}
      footer={null}
      width={calcWidth()}
      className="[&_.ant-modal-content]:!bg-transparent"
      styles={{
        mask: {
          zIndex: 9999,
        },
        wrapper: {
          zIndex: 9999,
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          padding: lg ? '32px' : tablet ? '32px 24px' : '28px 24px',
          minHeight: 'auto',
          background: '#1E1E2A',
          backdropFilter: 'blur(36.85px)',
          borderRadius: '24px',
          gap: '24px',
        },
      }}
    >
      <button
        onClick={onClose}
        className="absolute right-[16px] top-[16px] flex items-center justify-center rounded-full border border-[rgba(233,236,240,0.2)] p-2 transition-colors hover:bg-white/10"
      >
        <Image src="/icons/close-default.svg" width={20} height={20} alt="Close" />
      </button>

      {/* Header */}
      <div className="flex w-full flex-col items-center gap-4 px-12">
        <h2 className="font-inter text-[28px] md:text-[36px] font-bold leading-[1.12] text-[#E9ECF0] text-center">
          {`${tModal('congratulations')}!`}
        </h2>
      </div>

      {/* Body */}
      <div className="flex w-full flex-col gap-8">
        {/* Reward Section */}
        {(hasPoe || hasCoins) && (
          <div className="flex w-full flex-col items-center gap-4">
            <div className="font-inter text-[20px] font-medium leading-[1.2] text-[rgba(233,236,240,0.8)] text-center">
              {tModal('bid.earned')}
            </div>

            <div className="flex items-center justify-center gap-3">
              {hasPoe && (
                <div className="flex h-[72px] items-center gap-2 rounded-[80px] bg-[rgba(56,56,71,0.4)] pl-5 pr-7 py-[15px]">
                  <StatIcon type="fanTokens" className="h-10 w-10 shrink-0" />
                  <div className="font-inter text-[36px] font-semibold leading-[1.12] text-[#E9ECF0] whitespace-nowrap">
                    {formatNumber(reward.poe)}
                  </div>
                </div>
              )}
              {hasCoins && (
                <div className="flex h-[72px] items-center gap-2 rounded-[80px] bg-[rgba(56,56,71,0.4)] pl-5 pr-7 py-[15px]">
                  <StatIcon type="coins" className="h-10 w-10 shrink-0" />
                  <div className="font-inter text-[36px] font-semibold leading-[1.12] text-[#E9ECF0] whitespace-nowrap">
                    {formatNumber(reward.coins)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex w-full items-center justify-end pt-5">
          <button
            onClick={onClose}
            className="flex h-[60px] w-full items-center justify-center rounded-[14px] bg-[#E9ECF0] px-4 transition-colors hover:bg-[#E9ECF0]/90"
          >
            <span className="font-montserrat text-[19px] font-bold uppercase leading-[1.2] text-[#1E1E2A]">
              {tModal('ok')}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
});

ShareRewardModal.displayName = 'ShareRewardModal';
